// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Keyboard Shortcuts Modal
// Help modal displaying all available keyboard shortcuts
// ============================================================================

import { X, Keyboard } from 'lucide-react';
import { useEffect } from 'react';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; description: string }[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['G', 'D'], description: 'Go to Dashboard' },
      { keys: ['G', 'V'], description: 'Go to Verification Queue' },
      { keys: ['G', 'U'], description: 'Go to DUR Review' },
      { keys: ['G', 'F'], description: 'Go to Fill Queue' },
      { keys: ['G', 'C'], description: 'Go to Pharmacist Check' },
      { keys: ['G', 'R'], description: 'Go to Ready for Pickup' },
      { keys: ['G', 'S'], description: 'Go to Settings' },
    ],
  },
  {
    title: 'Queue Actions',
    shortcuts: [
      { keys: ['↑', '↓'], description: 'Navigate prescription list' },
      { keys: ['Enter'], description: 'Open selected prescription' },
      { keys: ['R'], description: 'Refresh queue' },
      { keys: ['/'], description: 'Focus search' },
      { keys: ['Esc'], description: 'Clear search / Close modal' },
    ],
  },
  {
    title: 'Prescription Workflow',
    shortcuts: [
      { keys: ['V'], description: 'Verify prescription' },
      { keys: ['A'], description: 'Approve / Accept' },
      { keys: ['X'], description: 'Reject prescription' },
      { keys: ['H'], description: 'Place on hold' },
      { keys: ['P'], description: 'Print label' },
      { keys: ['N'], description: 'Next prescription' },
    ],
  },
  {
    title: 'General',
    shortcuts: [
      { keys: ['?'], description: 'Show this help' },
      { keys: ['Ctrl', 'K'], description: 'Quick search' },
      { keys: ['Ctrl', 'S'], description: 'Save changes' },
      { keys: ['Ctrl', 'Z'], description: 'Undo last action' },
    ],
  },
];

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pharmacy-primary/10 flex items-center justify-center">
                <Keyboard className="h-5 w-5 text-pharmacy-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Keyboard Shortcuts</h2>
                <p className="text-sm text-slate-500">Speed up your workflow with these shortcuts</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
            <div className="grid md:grid-cols-2 gap-6">
              {SHORTCUT_GROUPS.map((group) => (
                <div key={group.title} className="space-y-3">
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                    {group.title}
                  </h3>
                  <div className="space-y-2">
                    {group.shortcuts.map((shortcut, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <span className="text-sm text-slate-600">{shortcut.description}</span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, keyIdx) => (
                            <span key={keyIdx} className="flex items-center gap-1">
                              <kbd className="px-2 py-1 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded shadow-sm min-w-[24px] text-center">
                                {key}
                              </kbd>
                              {keyIdx < shortcut.keys.length - 1 && (
                                <span className="text-slate-400 text-xs">+</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer tip */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Pro Tip:</strong> Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-amber-100 border border-amber-300 rounded">?</kbd> anytime to open this shortcut reference.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}



